import {
    getAdoptionInsights,
    getInitialState,
    buildRequestBody,
    computeAdoptionTimeSeries,
    computePreviousAdoptionTimeSeries,
    transformInsightsResponse,
    buildAppDataFromAdoptionState,
} from './adoption-trends.utils.js';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { getAppId } from '@/vuex/modules/trends/trends-settings.module.js';
import { StreamingProcessor } from '../ask-pendo/ask-pendo.streaming-processor.js';

const adoptionTrends = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setAdoptionInsights (state, { insights }) {
            state.insights = insights;
        },
        setInsightRequestBody (state, { requestBody }) {
            state.insightRequestBody = requestBody;
        },
        setLoading (state, { isLoading }) {
            state.isLoading = isLoading;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async fetchAdoptionInsights ({ commit, rootGetters, dispatch }, { options = {} } = {}) {
            commit('setLoading', { isLoading: true });
            commit('setError', { error: null });

            try {
                const appId = await getAppId(rootGetters, dispatch);

                await Promise.all([
                    dispatch('pages/loadAll', {}, { root: true }),
                    dispatch('features/loadAll', {}, { root: true }),
                ]);
                const countableIds = rootGetters['trendsSettings/getEvents']?.map((event) => {
                    return {
                        id: event.id,
                        kind: event.kind,
                    };
                });
                const includeAnonymous = hasSubscriptionFlag('showAnonymous');
                const currentTimeSeries = computeAdoptionTimeSeries(30);
                const previousTimeSeries = computePreviousAdoptionTimeSeries(currentTimeSeries);
                const requestBody = buildRequestBody(appId, countableIds, includeAnonymous, {
                    currentTimeSeries,
                    previousTimeSeries,
                });
                commit('setInsightRequestBody', { requestBody });

                const data = await getAdoptionInsights(requestBody, options);

                if (data instanceof ReadableStream) {
                    const streamingProcessor = new StreamingProcessor({
                        onData: (_chunk) => {},
                        onEnd: (finalState) => {
                            if (!finalState) {
                                commit('setError', { error: 'Stream ended without final state' });
                                commit('setLoading', { isLoading: false });

                                return;
                            }

                            const appData = buildAppDataFromAdoptionState(finalState, rootGetters, appId);

                            const transformedInsights = transformInsightsResponse(finalState, appData);

                            commit('setAdoptionInsights', { insights: transformedInsights });
                            commit('setLoading', { isLoading: false });
                        },
                        onError: (error) => {
                            commit('setError', { error: error.message || 'Failed to fetch adoption insights' });
                            commit('setLoading', { isLoading: false });
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                const appData = buildAppDataFromAdoptionState(data?.final_state, rootGetters, appId);

                const transformedInsights = transformInsightsResponse(data.final_state, appData);

                commit('setAdoptionInsights', { insights: transformedInsights });
                commit('setLoading', { isLoading: false });

                return transformedInsights;
            } catch (error) {
                commit('setError', { error: error.message || 'Failed to fetch adoption insights' });
                commit('setLoading', { isLoading: false });
                throw error;
            }
        },
    },
    getters: {
        adoptionInsights: (state) => state.insights || [],
        insightRequestBody: (state) => state.insightRequestBody,
        isLoading: (state) => state.isLoading,
        hasError: (state) => !!state.error,
        error: (state) => state.error,
    },
};

export default adoptionTrends;
